<template>
  <ConfrimCompetitionDeleteModal
    :confirm-modal-active="!!activeCompetitionToDelete"
    @onModalClose="onConfirmModalClose"
    @onDeleteCompetitionConfirm="onDeleteCompetitionConfirm"
  />
  <UpdateCompetitionResourcesModal
    v-if="!!competitionResourcesModalCompetition"
    :competition="competitionResourcesModalCompetition"
    @onClose="onCompetitionResourcesModalClose"
  />

  <div class="competition-configuration-table">
    <div class="competition-configuration-table__head">
      <div class="competition-configuration-table__row">
        <div
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-competition',
          ]"
        />
        <div
          v-for="feed in feeds"
          :key="feed"
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-feed',
          ]"
        >
          {{ getFeedName(feed) }}
        </div>
        <div
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-actions',
          ]"
        />
      </div>
      <div class="competition-configuration-table__row">
        <div
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-competition',
          ]"
        >
          <div class="competition-configuration-table__sort-cell">
            <CompetitionConfigurationTableSort
              :model-value="sort"
              @update:model-value="updateSort"
            />
            Competition
          </div>
        </div>
        <div
          v-for="feedColumn in feedColumns"
          :key="feedColumn.id"
          :class="[
            'competition-configuration-table__cell',
            `competition-configuration-table__cell--type-${feedColumn.type}`,
          ]"
        >
          {{ feedColumn.label }}
        </div>
        <div
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-actions',
          ]"
        />
      </div>
    </div>
    <div class="competition-configuration-table__body">
      <div
        v-for="competition in competitions"
        :key="competition.competitionId"
        class="competition-configuration-table__row"
      >
        <div
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-competition',
          ]"
        >
          <div
            class="competition-configuration-table__favorite-star"
            @click.stop="toggleFavorite(competition)"
          >
            <Icon
              :name="competition.isFavorite ? 'star-filled' : 'star'"
              class="competition-configuration-table__star-icon"
            />
          </div>
          <div class="competition-configuration-table__competition-info">
            <div class="competition-configuration-table__label">
              {{ competition.competitionName }}
            </div>
            <div class="competition-configuration-table__region">
              {{ competition.regionName }}
            </div>
          </div>
        </div>
        <div
          v-for="feedColumn in feedColumns"
          :key="feedColumn.id"
          :class="[
            'competition-configuration-table__cell',
            `competition-configuration-table__cell--type-${feedColumn.type}`,
          ]"
        >
          <CompetitionConfigurationPriorityField
            v-if="feedColumn.type === 'priority'"
            :key="getConfigurationPriority(`${competition.competitionId}:${feedColumn.feed}`)"
            variant="table"
            :disabled="updatingFeedPriorites"
            :model-value="getConfigurationPriority(`${competition.competitionId}:${feedColumn.feed}`)"
            @update:model-value="(priority) => updatePriority(`${competition.competitionId}:${feedColumn.feed}`, { priority })"
            :max="feeds.length"
            :min="1"
            :is-placeholder="!hasConfiguration(`${competition.competitionId}:${feedColumn.feed}`)"
          />
          <span
            v-else-if="hasConfiguration(`${competition.competitionId}:${feedColumn.feed}`)"
            class="competition-configuration-table__label competition-configuration-table__label--is-clickable"
            @click="editConfiguration(`${competition.competitionId}:${feedColumn.feed}`)"
          >
            {{ getConfigurationMarkets(`${competition.competitionId}:${feedColumn.feed}`) }}
          </span>
          <Button
            v-else
            icon="plus"
            @click="createConfiguration(competition.competitionId, competition.competitionName, feedColumn.feed)"
          >
            Configuration
          </Button>
        </div>
        <div
          :class="[
            'competition-configuration-table__cell',
            'competition-configuration-table__cell--type-actions',
          ]"
        >
          <Dropdown
            class="competition-configuration-table__actions-dropdown"
            icon="more-dots"
            hide-chevron
            right
          >
            <DropdownItem
              @click="updateCompetition(competition)"
              clickable
            >
              <Icon name="edit" />
              <span>Edit competition</span>
            </DropdownItem>
            <DropdownItem
              v-if="isSuperAdmin"
              @click="editCompetitionResources(competition)"
              clickable
            >
              <Icon name="signal" />
              <span>Edit competition resources</span>
            </DropdownItem>

            <DropdownItem
              class="dropdown-item--danger"
              @click="removeCompetition(competition.competitionId)"
              clickable
            >
              <Icon name="trash" />
              <span>Delete competition</span>
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
      <CompetitionConfigurationModal
        v-if="workingConfiguration"
        v-model:configuration="workingConfiguration"
        :original-configuration="originalConfiguration"
        :feeds="feeds"
        @submit="saveConfiguration"
        @close="closeConfiguration"
        @update:configuration="updateModalConfiguration"
      />
    </div>
  </div>
</template>

<script>
import {
  cloneDeep, capitalize, join, map,
  split, find, filter, each,
} from 'lodash';
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { allMarketCategories, makeFeedColumns, sortMarketCategoryCodes } from '@/services/helpers/competition-configuration';
import Button from '@/components/common/Button';
import CompetitionConfigurationTableSort from './CompetitionConfigurationTableSort';
import CompetitionConfigurationPriorityField from './CompetitionConfigurationPriorityField';
import CompetitionConfigurationModal from './CompetitionConfigurationModal';
import Dropdown from '@/components/common/Dropdown';
import DropdownItem from '@/components/common/DropdownItem';
import Icon from '@/components/common/Icon';
import ConfrimCompetitionDeleteModal from './ConfirmCompetitionDeleteModal';
import UpdateCompetitionResourcesModal from './UpdateCompetitionResourcesModal';
import { deleteCompetition, favoriteCompetition, unfavoriteCompetition } from '@/services/api/charon';
import { getSuperAdminData } from '@/services/helpers/super-admin';

export default {
  components: {
    Button,
    CompetitionConfigurationTableSort,
    CompetitionConfigurationPriorityField,
    CompetitionConfigurationModal,
    Dropdown,
    DropdownItem,
    Icon,
    ConfrimCompetitionDeleteModal,
    UpdateCompetitionResourcesModal,
  },
  props: {
    feeds: {
      type: Array,
      required: true,
    },
    competitions: {
      type: Array,
      required: true,
    },
    configurations: {
      type: Object,
      required: true,
    },
    sort: {
      type: String,
      default: '',
    },
    updatingFeedPriorites: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    'update:configurations': {
      type: Object,
    },
    'update:sort': {
      type: String,
    },
    'update:competition': {
      type: Object,
    },
    crudOperationCompleted: {
      type: String,
    },
    'update:competitionFeedPriority': {
      type: Object,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const route = useRoute();
    const originalConfiguration = ref(null);
    const workingConfiguration = ref(null);
    const competitionResourcesModalCompetition = ref(null);
    const { isSuperAdmin } = getSuperAdminData();

    const feedColumns = computed(() => makeFeedColumns(props.feeds));

    const getFeedName = (feed) => capitalize(feed);
    const hasConfiguration = (id) => !!props.configurations?.[id];
    const getConfigurationPriority = (id) => props.configurations?.[id]?.priority;
    const getConfigurationMarkets = (id) => {
      const markets = props.configurations?.[id]?.marketCategories || [];
      if (!markets.length) return '-';
      const orderedMarkets = sortMarketCategoryCodes(markets);
      return join(map(orderedMarkets, (market) => allMarketCategories?.[market]?.shortName || 'N/A'), ', ');
    };
    const updateSort = (newSort) => {
      emit('update:sort', newSort);
    };
    const updateConfiguration = (id, newFields = {}) => {
      emit('update:configurations', {
        ...props.configurations,
        [id]: {
          ...(props.configurations[id] || {}),
          ...newFields,
        },
      });
      emit('update:competitionFeedPriority', {
        competitionId: split(id, ':')[0],
        feed: split(id, ':')[1],
        priority: newFields.priority,
      });
    };

    const updateModalConfiguration = (newFields = {}) => {
      workingConfiguration.value = {
        ...(workingConfiguration.value || {}),
        ...newFields,
      };
    };

    const updatePriority = (id, newFields = {}) => {
      const competitionId = split(id, ':')[0];
      const newPriority = newFields.priority;

      const conflictingFeed = find(props.feeds, (feed) => {
        const configId = `${competitionId}:${feed}`;
        const currentPriority = props.configurations[configId]?.priority;
        return `${currentPriority}` === newPriority;
      });
      if (conflictingFeed) {
        // Swap priorities
        const conflictingId = `${competitionId}:${conflictingFeed}`;
        const currentPriority = props.configurations[id]?.priority;

        emit('update:configurations', {
          ...props.configurations,
          [conflictingId]: {
            ...(props.configurations[conflictingId] || {}),
            priority: currentPriority,
          },
          [id]: {
            ...(props.configurations[id] || {}),
            ...newFields,
          },
        });
        emit('update:competitionFeedPriority', {
          competitionId,
          feed: split(conflictingId, ':')[1],
          priority: currentPriority,
        });
        emit('update:competitionFeedPriority', {
          competitionId,
          feed: split(id, ':')[1],
          priority: newPriority,
        });
      } else {
        // No conflict, just update the priority
        updateConfiguration(id, newFields);
      }
    };
    const createConfiguration = (competitionId, competitionName, feed) => {
      let priority = 1;
      const sameCompetitionConfigurations = filter(props.configurations, (config) => config.competitionId === competitionId);
      each(sameCompetitionConfigurations, (config) => {
        if (config.priority >= priority) {
          priority = Number(config.priority) + 1;
        }
      });
      originalConfiguration.value = null;
      workingConfiguration.value = {
        competitionId,
        competitionName,
        coverageExpected: false,
        feed,
        id: `${competitionId}:${feed}`,
        marketCategories: [],
        priority,
        unallowedPriorities: map(sameCompetitionConfigurations, (config) => config.priority),
      };
    };
    const closeConfiguration = () => {
      originalConfiguration.value = null;
      workingConfiguration.value = null;
    };
    const editConfiguration = (id) => {
      originalConfiguration.value = props.configurations[id];
      workingConfiguration.value = cloneDeep(props.configurations[id]);
    };
    const saveConfiguration = () => {
      if (!workingConfiguration.value) return;
      updateConfiguration(workingConfiguration.value.id, workingConfiguration.value);
      closeConfiguration();
    };

    const activeCompetitionToDelete = ref(null);
    const removeCompetition = (competitionId) => {
      activeCompetitionToDelete.value = competitionId;
    };

    const onConfirmModalClose = () => {
      activeCompetitionToDelete.value = null;
    };

    const onDeleteCompetitionConfirm = () => {
      deleteCompetition(activeCompetitionToDelete.value)
        .then(() => {
          store.dispatch('addNotification', {
            message: 'Competition successfully created!',
            type: 'success',
            duration: 5000,
          });
          emit('crudOperationCompleted');
        })
        .catch(() => {
          store.dispatch('addNotification', {
            message: 'Something went wrong when deleting competition. Please try again later.',
            type: 'error',
            duration: 5000,
          });
        }).finally(() => {
          activeCompetitionToDelete.value = null;
        });
    };
    const updateCompetition = (competitition) => {
      emit('update:competition', competitition);
    };

    const onCompetitionResourcesModalClose = () => {
      competitionResourcesModalCompetition.value = null;
    };

    const editCompetitionResources = (competition) => {
      competitionResourcesModalCompetition.value = competition;
    };

    const toggleFavorite = async (competition) => {
      const action = competition.isFavorite ? unfavoriteCompetition : favoriteCompetition;
      const payload = competition.isFavorite
        ? { competitionId: competition.competitionId }
        : { competitionId: competition.competitionId, sportId: route.query.sport };
      try {
        await action(payload);
        store.dispatch('addNotification', {
          message: `Successfully ${competition.isFavorite ? 'removed from' : 'added to'} favorites`,
          type: 'success',
          duration: 5000,
        });
        emit('crudOperationCompleted'); // triggers reload
      } catch {
        store.dispatch('addNotification', {
          message: `Failed to ${competition.isFavorite ? 'remove from' : 'add to'} favorites`,
          type: 'error',
          duration: 5000,
        });
      }
    };

    return {
      originalConfiguration,
      workingConfiguration,
      feedColumns,
      activeCompetitionToDelete,
      getFeedName,
      hasConfiguration,
      getConfigurationPriority,
      getConfigurationMarkets,
      updateSort,
      updateConfiguration,
      createConfiguration,
      closeConfiguration,
      editConfiguration,
      saveConfiguration,
      removeCompetition,
      onConfirmModalClose,
      onDeleteCompetitionConfirm,
      updateCompetition,
      updatePriority,
      updateModalConfiguration,
      editCompetitionResources,
      competitionResourcesModalCompetition,
      onCompetitionResourcesModalClose,
      isSuperAdmin,
      toggleFavorite,
    };
  },
};
</script>

<style lang="scss">
.competition-configuration-table {
  &__row {
    display: flex;
    padding: 0 8px;
    background-color: inherit;
    color: inherit;
  }

  &__cell {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    padding: 0 8px;
    background-color: inherit;
    color: inherit;
    font-family: 'Rubik', sans-serif;
    font-weight: 400px;
    font-size: 14px;
    line-height: 16px;

    &:first-of-type {
      position: sticky;
      left: -16px;
    }

    &--type-competition {
      min-width: 150px;
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      align-items: center;
    }

    &--type-feed {
      width: 240px;
      min-width: 240px;
      max-width: 240px;
      color: #A9A9A9;
    }

    &--type-priority {
      width: 80px;
      min-width: 80px;
      max-width: 80px;
      margin: 8px 0;
    }

    &--type-configuration {
      width: 160px;
      min-width: 160px;
      max-width: 160px;
    }

    &--type-actions {
      display: flex;
      align-items: flex-end;
      width: 80px;
      min-width: 80px;
      max-width: 80px;

      .competition-configuration-table__actions-dropdown {
        z-index: unset;
        .button {
          padding: 0;
          width: unset;
          border: none;
        }
        .dropdown-item {
          padding: 8px;
          display: flex;
          align-items: center;
          gap: 4px;

          &--danger {
            color: $error500;
            svg path {
              fill: $error500;
            }
          }
        }
      }
    }
  }

  &__head {
    display: flex;
    flex-direction: column;
    background-color: #FAFAFA;
    color: #6D6D6D;
    border-radius: 4px;
    position: sticky;
    top: 0;
    z-index: 100;

    .competition-configuration-table__row {
      height: 32px;
      min-height: 32px;
      max-height: 32px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    color: #191414;

    .competition-configuration-table__row {
      height: 44px;
      min-height: 44px;
      max-height: 44px;
      border-bottom: 1px solid #F0F0F0;
    }
  }

  &__competition-container {
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__label {
    font-family: 'Rubik', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    color: #191414;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &--is-small {
      font-size: 12px;
      line-height: 14.22px;
    }

    &--is-muted {
      color: #6D6D6D;
    }

    &--is-clickable {
      cursor: pointer;
    }
  }

  &__sort-cell {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 100%;
    height: 100%;
  }

  &__favorite-star {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    cursor: pointer;

    .competition-configuration-table__star-icon {
      width: 16px;
      height: 16px;
    }
  }

  &__competition-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__region {
    font-size: 12px;
    line-height: 14px;
    color: #A9A9A9;
  }
}
</style>
